<template>
    <NuxtLayout name="main" :dark="true">
        <div :class="css.container" class="container pt-[64px]">
            <!-- * 顶部内容 -->
            <section>
                <div class="text-emphas text-[44px] leading-[76px] font-bold">{{ $t("MutationAlPredictorFlow") }}</div>
                <div class="flex flex-wrap mx-[-6px] mb-[-6px] mt-[2px]">
                    <div class="m-[6px] flex items-center rounded-full min-h-[40px] px-[16px] bg-color4 leading-[22px]">
                        {{ $t("MutationViewer") }}
                    </div>
                    <div class="m-[6px] flex items-center rounded-full min-h-[40px] px-[16px] bg-color4 leading-[22px]">
                        {{ $t("VPP") }} & {{ $t("RNASplicer") }}
                    </div>
                    <div class="m-[6px] flex items-center rounded-full min-h-[40px] px-[16px] bg-color4 leading-[22px]">{{ $t("ASO") }}</div>
                </div>

                <div class="flex justify-center bg-[#D9DFF2] pt-[80px] pb-[88px] mt-[40px]">
                    <div class="relative pb-[34px]">
                        <img class="w-[96px] h-[96px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/0b728c45-10c4-4ffe-8be3-86366f3c37ce.webp" />
                        <div
                            class="whitespace-nowrap absolute bottom-0 left-1/2 text-emphas text-[18px] leading-[34px] font-bold"
                            style="transform: translateX(-50%)">
                            {{ $t("GeneticInformation") }}
                        </div>
                    </div>
                    <img class="w-[136px] h-[16px] mx-[32px] mt-[40px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/6ad751b7-12e9-4c82-a1ae-f49d7fa32e72.webp" />
                    <div class="relative pb-[34px]">
                        <img class="w-[96px] h-[96px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/355370fc-a348-4bbe-b5da-3618b175213a.webp" />
                        <div
                            class="whitespace-nowrap absolute bottom-0 left-1/2 text-emphas text-[18px] leading-[34px] font-bold"
                            style="transform: translateX(-50%)">
                            {{ $t("MutationSites") }}
                        </div>
                    </div>
                    <img class="w-[136px] h-[16px] mx-[32px] mt-[40px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/b270d254-cc92-4da5-993a-65d50a073f16.webp" />
                    <div class="relative pb-[34px]">
                        <img class="w-[96px] h-[96px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/abff70d0-1653-480f-8a8b-ae897573186b.webp" />
                        <div
                            class="whitespace-nowrap absolute bottom-0 left-1/2 text-emphas text-[18px] leading-[34px] font-bold"
                            style="transform: translateX(-50%)">
                            {{ $t("PredictionResults") }}
                        </div>
                    </div>
                    <img class="w-[136px] h-[16px] mx-[32px] mt-[40px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/c0fc414c-6430-44f7-8462-ade792f13a34.webp" />
                    <div class="relative pb-[34px]">
                        <img class="w-[96px] h-[96px]" src="https://rdgta.cdn.cyagen.com/rddc/pictures/abbef19d-ac5b-4764-854c-1cdc94e0e467.webp" />
                        <div
                            class="whitespace-nowrap absolute bottom-0 left-1/2 text-emphas text-[18px] leading-[34px] font-bold"
                            style="transform: translateX(-50%)">
                            {{ $t("ASODesign") }}
                        </div>
                    </div>
                </div>
            </section>

            <!-- * 模块一 -->
            <section class="mt-[40px]">
                <div class="text-emphas text-[24px] leading-[40px] font-bold">01. {{ $t("SearchForMutation") }}</div>
                <div class="mt-[4px]">{{ $t("WorkflowStep1Descript") }}</div>
                <div class="mt-[16px] py-[24px] px-[48px] bg-color7" v-loading="data.search.load">
                    <div class="table">
                        <div class="table-row">
                            <div class="table-cell">{{ useColon($t("Gene")) }}</div>
                            <div class="table-cell">
                                <default-search
                                    class="ml-[16px]"
                                    v-model="data.search.name"
                                    mode="select"
                                    type="gene"
                                    @search="(response) => methods.change(data.search, response)"
                                    :width="440"
                                    :query="{ tax_names: ['Human'] }"
                                    :placeholder="$t('PleaseEnterGeneNameOrNCBlID')" />
                            </div>
                            <div class="table-cell">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.search.id && data.search.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/0bc96931-7a3c-44ca-8d2b-7f8a0a070e5b.webp" />
                                <img
                                    class="ml-[16px]"
                                    v-if="data.search.id && !data.search.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/01830a79-afd0-4594-accd-fe12539789e1.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]">{{ useColon($t("Transcript")) }}</div>
                            <div class="table-cell pt-[16px]">
                                <ui-select
                                    class="ml-[16px]"
                                    v-model="data.search.transcript"
                                    :width="440"
                                    :disabled="!data.search.transcript.length"
                                    :candidates="data.search.transcripts"
                                    :placeholder="$t('PleaseSelectTranscript')"
                                    @change="methods.transcript(data.search)" />
                            </div>
                            <div class="table-cell pt-[16px]">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.search.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/08299c6b-5f22-4777-b94c-19edeed3d7d0.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]">
                                <span>{{ useColon($t("Mutation")) }}</span>
                                <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                    <render-mutation-describe />
                                </helper-modal>
                            </div>
                            <div class="table-cell pt-[16px]">
                                <ui-input
                                    class="ml-[16px]"
                                    v-model="data.search.keyword"
                                    :width="440"
                                    :disabled="!data.search.transcripts.length"
                                    :maxlength="50"
                                    :placeholder="$t('PleaseEnterMutation')" />
                            </div>
                            <div class="table-cell pt-[16px]">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.search.status == 'success'"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/6cb54d5a-0893-4ff4-ba70-5ced36de11ae.webp" />
                                <img
                                    class="ml-[16px]"
                                    v-if="data.search.status == 'error'"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/25e7737f-275c-4fc3-8b95-4fa64431d7e5.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]"></div>
                            <div class="table-cell pt-[16px]">
                                <ui-button
                                    class="ml-[16px]"
                                    :width="104"
                                    :disabled="!data.search.id || !data.search.keyword || !data.search.transcript"
                                    @click="methods.search(data.search)">
                                    {{ $t("Search") }}
                                </ui-button>
                                <ui-button class="ml-[16px]" :width="104" :class="css_button.cancel" @click="methods.reset(data.search)">
                                    {{ $t("Reset") }}
                                </ui-button>
                            </div>
                            <div class="table-cell pt-[16px]"></div>
                        </div>
                    </div>

                    <!-- * 结果 -->
                    <div class="mt-[24px]" v-if="data.search.response">
                        <div class="mb-[24px] w-full h-[1px] bg-uicolor4/50"></div>
                        <div>
                            <span>{{ useColon($t("Result")) }}&nbsp;</span>
                            <ProjectRoute
                                :class="css_normalize.assets"
                                :to="{
                                    name: 'BioVariantResult',
                                    params: { id: data.search.response.id },
                                    query: { id: data.search.response.transcript, keyword: data.search.response.keyword },
                                }">
                                {{ $t("ClickHereToViewMutation") }} - {{ data.search.response.name }}: {{ data.search.response.keyword }}
                            </ProjectRoute>
                        </div>
                    </div>
                </div>
            </section>

            <!-- * 模块二 -->
            <section class="mt-[40px]">
                <div class="text-emphas text-[24px] leading-[40px] font-bold">02. {{ $t("AIDrivenModelForPrediction") }}</div>
                <div class="mt-[4px]">{{ $t("WorkflowStep2Descript") }}</div>
                <div class="mt-[16px] py-[24px] px-[48px] bg-color7" v-loading="data.prediction.load">
                    <!-- * 选择项 -->
                    <div>
                        <!-- * 预测模式 -->
                        <ui-radio-group v-model="data.prediction.mode">
                            <ui-radio class="text-emphas text-[16px] leading-[32px] font-bold" value="RNA Splicer">{{ $t("RNASplicer") }}</ui-radio>
                            <ui-radio class="text-emphas text-[16px] leading-[32px] font-bold ml-[40px]" value="Pathogenicity Predictor">
                                {{ $t("VPP") }}
                            </ui-radio>
                        </ui-radio-group>

                        <!-- * RNA Splicer描述文本 -->
                        <div class="mt-[8px]" v-if="data.prediction.mode == 'RNA Splicer'">
                            <div>{{ $t("RNASplicerDescript2") }}</div>
                            <div>{{ $t("RNASplicerDescript3") }}</div>
                        </div>

                        <!-- * 致病性预测模型描述文本 -->
                        <div class="mt-[8px]" v-if="data.prediction.mode == 'Pathogenicity Predictor'">
                            <div>{{ $t("VPPDescript1") }}</div>
                            <div>{{ $t("VPPDescript2") }}</div>
                        </div>

                        <!-- * 获取第一步数据 -->
                        <div
                            class="mt-[8px] cursor-pointer text-main underline underline-offset-2"
                            v-if="data.search.status == 'success'"
                            @click="methods.obtain(data.prediction)">
                            {{ $t("GetMutationFromStep1") }} &gt;&gt;
                        </div>
                    </div>

                    <!-- * 输入模型 -->
                    <div class="table mt-[24px]">
                        <div class="table-row">
                            <div class="table-cell">{{ useColon($t("Gene")) }}</div>
                            <div class="table-cell">
                                <default-search
                                    class="ml-[16px]"
                                    v-model="data.prediction.name"
                                    mode="select"
                                    type="gene"
                                    ref="ai_input"
                                    @search="(response) => methods.change(data.prediction, response)"
                                    :width="440"
                                    :query="{ tax_names: ['Human'] }"
                                    :placeholder="$t('PleaseEnterGeneNameOrNCBlID')" />
                            </div>
                            <div class="table-cell">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.prediction.id && data.prediction.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/39a0ea85-cd19-4e1c-9565-78a6dfdcecd2.webp" />
                                <img
                                    class="ml-[16px]"
                                    v-if="data.prediction.id && !data.prediction.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/68fb06ae-b1f3-40b2-a355-6a7cdfa2159b.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]">{{ useColon($t("Transcript")) }}</div>
                            <div class="table-cell pt-[16px]">
                                <ui-select
                                    class="ml-[16px]"
                                    v-model="data.prediction.transcript"
                                    :width="440"
                                    :disabled="!data.prediction.transcript.length"
                                    :candidates="data.prediction.transcripts"
                                    :placeholder="$t('PleaseSelectTranscript')"
                                    @change="methods.transcript(data.prediction)" />
                            </div>
                            <div class="table-cell pt-[16px]">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.prediction.transcript"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/6e68a87d-b5e0-4108-9e6e-bd3d28301fb9.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]">
                                <span>{{ useColon($t("Mutation")) }}</span>
                                <helper-modal class="ml-[4px]" :width="400" :size="16" :icon="10" theme="grey">
                                    <render-mutation-describe />
                                </helper-modal>
                            </div>
                            <div class="table-cell pt-[16px]">
                                <ui-input
                                    class="ml-[16px]"
                                    v-model="data.prediction.keyword"
                                    :width="440"
                                    :disabled="!data.prediction.transcripts.length"
                                    :maxlength="50"
                                    :placeholder="$t('PleaseEnterMutation')" />
                            </div>
                            <div class="table-cell pt-[16px]">
                                <img
                                    class="ml-[16px]"
                                    v-if="data.prediction.status == 'success'"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/abaa9a06-a738-4290-a7c8-01ce77716626.webp" />
                                <img
                                    class="ml-[16px]"
                                    v-if="data.prediction.status == 'error'"
                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/a48a48d3-a2d5-47ae-b045-836310fc5893.webp" />
                            </div>
                        </div>

                        <div class="table-row">
                            <div class="table-cell pt-[16px]"></div>
                            <div class="table-cell pt-[16px]">
                                <ui-button
                                    class="ml-[16px]"
                                    :width="104"
                                    :disabled="!data.prediction.id || !data.prediction.keyword || !data.prediction.transcript"
                                    @click="methods.search(data.prediction)">
                                    {{ $t("Search") }}
                                </ui-button>
                                <ui-button class="ml-[16px]" :width="104" :class="css_button.cancel" @click="methods.reset(data.prediction)">
                                    {{ $t("Reset") }}
                                </ui-button>
                            </div>
                            <div class="table-cell pt-[16px]"></div>
                        </div>
                    </div>

                    <!-- * 结果 -->
                    <div class="mt-[24px]" v-if="data.prediction.response">
                        <div class="mb-[24px] w-full h-[1px] bg-uicolor4/50"></div>
                        <div>
                            <span>{{ useColon($t("Result")) }}&nbsp;</span>
                            <div :class="css_normalize.assets" @click="methods.prediction(data.prediction)">
                                {{ data.prediction.mode == "RNA Splicer" ? $t("ClickHereToViewRNASplicer") : $t("ClickHereToViewPathogenicity") }}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- * 模块三 -->
            <section class="mt-[40px]">
                <div class="text-emphas text-[24px] leading-[40px] font-bold">03. {{ $t("ASODesignModel") }}</div>
                <div class="mt-[4px]">{{ $t("WorkflowStep3Descript") }}</div>
            </section>
        </div>
    </NuxtLayout>
</template>

<script lang="tsx" setup>
//* 组件库
import { UiInput, UiSelect, UiButton, UiRadio, UiRadioGroup } from "various-ui";
//* 公共组件
import { DefaultSearch } from "@/components/controls";
import { HelperModal } from "@/components/windows";
//* 组件属性
import { useComposable } from "./src/composable";
//* 样式
import css_normalize from "@/assets/style/src/index.module.less";
import css_button from "@/assets/style/src/button.module.less";
import css from "./index.module.less";

//* 插件
const i18n = useI18n();

//* 组合函数
const { data, nodes, methods } = useComposable();
const { ai_input } = nodes;

//* 渲染函数（突变模板格式说明）
const renderMutationDescribe = defineComponent({
    render() {
        return (
            <div>
                <div>{i18n.t("FastExpressDescribe1")}</div>
                <div>（1）c.123G&gt;A</div>
                <div>（2）c.123_124TG&gt;C</div>
                <div>（3）c.169+2T&gt;C</div>
                <div>（4）c.45_46insGC</div>
                <div>（5）c.36_38del</div>
                <div>（6）c.45_47delinsTT</div>
                <div>（7）c.-1G&gt;T</div>
                <div>（8）c.*16G&gt;T</div>
                <div>（9）c.23+4_23+5GG&gt;CT</div>
                <div>（10）p.Ser3435Ter</div>
                <div style="margin-top: 10px">{i18n.t("FastExpressDescribe2")}</div>
            </div>
        );
    },
});
</script>
